import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Stack } from "@astryxdesign/core/Stack";
import { Grid } from "@astryxdesign/core/Grid";
import { Card } from "@astryxdesign/core/Card";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import {
  MetadataList,
  MetadataListItem,
} from "@astryxdesign/core/MetadataList";
import { downloadsAPI } from "../../services/api";

export default function FileViewer({ file, onClose }) {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = async () => {
    try {
      setIsLoading(true);
      const storagePath = file.path || file.name;
      const result = await downloadsAPI.getSignedUrl(
        storagePath,
        3600,
        file.name
      );
      if (result && result.url) {
        const response = await fetch(result.url);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = file.name;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(objectUrl);
      } else {
        console.error("Signed URL not returned for", storagePath);
      }
    } catch (e) {
      console.error("Failed to download file:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Tem certeza que deseja deletar "${file.name}"?`)) {
      // Deleting file
      onClose();
    }
  };

  const formatFileSize = (size) => {
    if (size === undefined || size === null) return "-";
    const n = Number(size);
    if (Number.isNaN(n)) return String(size);
    const units = ["B", "KB", "MB", "GB", "TB"];
    let i = 0;
    let val = n;
    while (val >= 1024 && i < units.length - 1) {
      val /= 1024;
      i++;
    }
    return `${val.toFixed(val < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("pt-BR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const subtitle = [
    file.company,
    formatFileSize(file.size),
    file.file_type || file.mimeType || "-",
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      width={760}
    >
      <Stack gap={3}>
        <DialogHeader
          title={file.name}
          subtitle={subtitle}
          onOpenChange={(open) => {
            if (!open) onClose();
          }}
          hasDivider
        />
        <Grid columns={{ minWidth: 280, max: 2 }} gap={4}>
          {/* Área de preview (placeholder) */}
          <Stack gap={2}>
            <Text weight="semibold">Visualização</Text>
            <Card padding={6}>
              <Stack gap={1} hAlign="center">
                <Text color="accent">
                  <FontAwesomeIcon icon="file-alt" size="3x" />
                </Text>
                <Text>Preview do PDF</Text>
                <Text type="supporting">
                  Aqui seria exibido o preview do arquivo PDF
                </Text>
              </Stack>
            </Card>
          </Stack>

          {/* Informações e ações */}
          <Stack gap={3}>
            <MetadataList title="Informações do Arquivo" columns={1}>
              <MetadataListItem label="Nome">{file.name}</MetadataListItem>
              <MetadataListItem label="Empresa">
                {file.company || "-"}
              </MetadataListItem>
              <MetadataListItem label="Tamanho">
                {formatFileSize(file.size)}
              </MetadataListItem>
              <MetadataListItem label="Data de Criação">
                {formatDate(file.date)}
              </MetadataListItem>
              <MetadataListItem label="Data de Emissão">
                {formatDate(file.emissionDate)}
              </MetadataListItem>
              <MetadataListItem label="Tipo">
                {file.file_type || file.mimeType || "-"}
              </MetadataListItem>
            </MetadataList>

            <Stack gap={2}>
              <Button
                label={isLoading ? "Baixando..." : "Baixar Arquivo"}
                variant="primary"
                icon={<FontAwesomeIcon icon="download" />}
                isLoading={isLoading}
                onClick={handleDownload}
              />
              <Button
                label="Compartilhar"
                variant="secondary"
                icon={<FontAwesomeIcon icon="share" />}
              />
              <Button
                label="Deletar Arquivo"
                variant="destructive"
                icon={<FontAwesomeIcon icon="trash" />}
                onClick={handleDelete}
              />
            </Stack>
          </Stack>
        </Grid>
      </Stack>
    </Dialog>
  );
}
